import clsx from 'clsx'
import { saveAs } from 'file-saver'
import { useAtomCallback } from 'jotai/utils'
import { useCallback, type ComponentPropsWithoutRef, type FC } from 'react'
import { FaUpload } from 'react-icons/fa'

import { CsvActionButton } from '@/components/elements'
import { Button, Title } from '@/components/elements'
import {
  ACTIONS,
  ERROR_MESSAGES,
  LABELS
} from '@/constants/common/message.constants'
import { toastApiErrorMessage } from '@/features/shared/utils'
import { exportShipmentOrders } from '@/features/shipment-orders/api'

import { queryFrom, type ShipmentOrdersSearch } from '../../-models'
import { isOpenShipmentOrderCsvImportModalAtom } from '../../../-states'

type Props = ComponentPropsWithoutRef<'div'> & {
  tenantKey: string
  search: ShipmentOrdersSearch
}

/**
 * 配送依頼一覧のヘッダー
 */
export const ShipmentOrdersHeader: FC<Props> = ({
  className,
  tenantKey,
  search,
  ...props
}) => {
  // 配送依頼のインポートの入口は 1 つ。取り込み方法（サントリー様の社内移動・得意先を含む）は
  // 開いたモーダルの最初の画面で選ぶ（#4728）
  const handleOpenImportModal = useAtomCallback(
    useCallback((_, set) => {
      set(isOpenShipmentOrderCsvImportModalAtom, true)
    }, [])
  )

  const formatTimestamp = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')

    return `${year}${month}${day}${hours}${minutes}${seconds}`
  }

  const handleShipmentDownload = async () => {
    try {
      const csvBlob = await exportShipmentOrders({
        tenantKey,
        query: queryFrom(search)
      })
      // 空レスポンスを saveAs に渡すと中身の無いファイルが落ちて成功と区別できない。
      // 失敗として catch に流し、通知だけを出す（運行実績の ToursHeader と同じ扱い）
      if (!csvBlob) {
        throw new Error(ERROR_MESSAGES.FAILED_TO_DOWNLOAD)
      }
      const fileName = `shipment-orders_${formatTimestamp(new Date())}.csv`
      saveAs(csvBlob, fileName)
    } catch (error) {
      toastApiErrorMessage(error, ERROR_MESSAGES.FAILED_TO_DOWNLOAD_CSV)
    }
  }

  return (
    <div {...props} className={clsx(['flex gap-2', className])}>
      <Title className="flex-1" size="large">
        {LABELS.SHIPMENT_ORDER}
      </Title>
      <CsvActionButton
        action="export"
        type="default"
        onClick={handleShipmentDownload}
      />
      <Button
        type="secondary"
        icon={<FaUpload className="opacity-80" />}
        onClick={handleOpenImportModal}
      >
        {ACTIONS.IMPORT_SHIPMENT_ORDER}
      </Button>
    </div>
  )
}
